import { API_BASE_URL } from '../config/apiConfig';

interface HealthStatus {
  status: 'healthy' | 'degraded' | 'unhealthy' | 'checking' | 'offline';
  timestamp?: string;
  version?: string;
  environment?: string;
  uptime?: {
    seconds: number;
    human: string;
  };
  responseTime?: number;
  services?: {
    database?: {
      status: string;
      responseTime: number;
      connection: string;
    };
    server?: {
      status: string;
      memory: {
        rss: number;
        heapTotal: number;
        heapUsed: number;
        external: number;
      };
      pid: number;
      nodeVersion: string;
      platform: string;
    };
  };
  checks?: {
    database?: boolean;
    memory?: boolean;
    uptime?: boolean;
  };
}

interface HealthCheckOptions {
  timeout?: number;
  retries?: number;
  interval?: number;
}

class HealthCheckService {
  private baseUrl: string;
  private timeout: number;
  private retries: number;
  private interval: number;
  private currentStatus: HealthStatus = { status: 'checking' };
  private listeners: Array<(status: HealthStatus) => void> = [];
  private intervalId: NodeJS.Timeout | null = null;

  constructor(options: HealthCheckOptions = {}) {
    this.baseUrl = API_BASE_URL;
    this.timeout = options.timeout || 5000; // 5 seconds
    this.retries = options.retries || 2;
    this.interval = options.interval || 30000; // 30 seconds
  }

  // Start periodic health checks
  start() {
    if (this.intervalId) {
      this.stop();
    }

    // Initial check
    this.checkHealth();

    // Set up periodic checks
    this.intervalId = setInterval(() => {
      this.checkHealth();
    }, this.interval);
  }

  // Stop periodic health checks
  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  // Get current status
  getStatus(): HealthStatus {
    return this.currentStatus;
  }

  // Subscribe to status changes
  subscribe(listener: (status: HealthStatus) => void) {
    this.listeners.push(listener);
    
    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  // Notify all listeners of status change
  private notifyListeners(status: HealthStatus) {
    this.listeners.forEach(listener => {
      try {
        listener(status);
      } catch (error) {
        console.error('Health check listener error:', error);
      }
    });
  }

  // Perform health check
  async checkHealth(): Promise<HealthStatus> {
    const status: HealthStatus = { status: 'checking' };
    this.currentStatus = status;
    this.notifyListeners(status);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const response = await fetch(`${this.baseUrl}/health`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        this.currentStatus = data;
        this.notifyListeners(data);
        return data;
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Health check failed:', error);
      
      const errorStatus: HealthStatus = {
        status: 'offline',
        timestamp: new Date().toISOString(),
      };
      
      this.currentStatus = errorStatus;
      this.notifyListeners(errorStatus);
      return errorStatus;
    }
  }

  // Quick ping check
  async ping(): Promise<boolean> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout for ping

      const response = await fetch(`${this.baseUrl}/ping`, {
        method: 'GET',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      return response.ok;
    } catch (error) {
      console.error('Ping failed:', error);
      return false;
    }
  }

  // Get status summary for display
  getStatusSummary(): {
    status: HealthStatus['status'];
    message: string;
    details: string;
    icon: string;
    color: string;
  } {
    const status = this.currentStatus.status;
    
    switch (status) {
      case 'healthy':
        return {
          status: 'healthy',
          message: 'All systems operational',
          details: `Backend v${this.currentStatus.version || '1.0.0'} • Uptime: ${this.currentStatus.uptime?.human || 'Unknown'}`,
          icon: '🟢',
          color: '#4caf50'
        };
      
      case 'degraded':
        return {
          status: 'degraded',
          message: 'Service degraded',
          details: 'Some services may be experiencing issues',
          icon: '🟡',
          color: '#ff9800'
        };
      
      case 'unhealthy':
        return {
          status: 'unhealthy',
          message: 'Service unhealthy',
          details: 'Critical services are down',
          icon: '🔴',
          color: '#f44336'
        };
      
      case 'checking':
        return {
          status: 'checking',
          message: 'Checking status...',
          details: 'Connecting to backend services',
          icon: '🔄',
          color: '#2196f3'
        };
      
      case 'offline':
      default:
        return {
          status: 'offline',
          message: 'Backend offline',
          details: 'Unable to connect to server',
          icon: '⚫',
          color: '#757575'
        };
    }
  }
}

// Create singleton instance
export const healthCheck = new HealthCheckService();

export default HealthCheckService;
export type { HealthStatus, HealthCheckOptions };
